import Modal from 'LuxKit/Modal/Modal'
import Table from 'LuxKit/Tables/Table'
import TableBody from 'LuxKit/Tables/TableBody'
import TableBodyCell from 'LuxKit/Tables/TableBodyCell'
import TableBodyRow from 'LuxKit/Tables/TableBodyRow'
import TableHeader from 'LuxKit/Tables/TableHeader'
import TableHeaderCell from 'LuxKit/Tables/TableHeaderCell'
import TableHeaderRow from 'LuxKit/Tables/TableHeaderRow'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import Colour from 'components/utils/Colour'
import FormatDecimal from 'components/utils/Formatters/FormatDecimal'
import Group from 'components/utils/Group'
import useLoyaltyPointsEarnCalculator from 'loyalty/hooks/useLoyaltyPointsEarnCalculator'
import determineLoyaltyBonusPointsCampaignType from 'loyalty/lib/determineLoyaltyBonusPointsCampaignType'
import getLoyaltyProductTypeIcon from 'loyalty/lib/getLoyaltyProductTypeIcon'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import React, { useMemo } from 'react'
import LoyaltyBonusPointsInfoSheet from '../Banners/LoyaltyBonusPointsInfoSheet'
import FormatLoyaltyPoints from '../Formatters/FormatLoyaltyPoints'

function getItemTitle(productType: App.LoyaltyProductType | undefined): string {
  switch (productType) {
    case 'dom_economy':
    case 'dom_business':
    case 'int_economy':
    case 'int_business':
    case 'int_first':
    case 'int_premium_economy':
      return 'Flight'
    case 'tour_bank':
    case 'le_tour':
    case 'trusted_partner_tour':
      return 'Tour'
    case 'tour_optional_extra':
      return 'Tour optional extras'
    case 'cfmr':
    case 'insurance':
      return 'Insurance/Coverage'
    case 'cruise_group_1':
    case 'cruise_group_2':
    case 'cruise_group_3':
      return 'Cruise'
    case 'experience':
      return 'Experience'
    case 'car_hire':
      return 'CarHire'
    case 'lux_plus':
      return LUXURY_PLUS.PROGRAM_NAME
    case 'hotel_flash':
    case 'hotel_lpc':
    case 'hotel_lpp_eps':
    case 'rental':
      return 'Room'
    default:
      return ''
  }
}

interface Props {
  title: string
  calculationRequests?: Array<App.LoyaltyPointsEarnCalculationRequest | undefined> | App.LoyaltyPointsEarnCalculationRequest | undefined
}

function LoyaltyPointsEarnBreakdownModal({ title, calculationRequests }: Props) {
  const { pointsCalculations, totalPointsCalculation } = useLoyaltyPointsEarnCalculator(calculationRequests)
  const bonusPointsCampaign = totalPointsCalculation.data ? determineLoyaltyBonusPointsCampaignType(totalPointsCalculation.data) : undefined
  const pointsCalculationsToDisplay = useMemo(() => {
    return pointsCalculations.filter(calculation => {
      return calculation?.data && (calculation.data.basePoints || calculation.data.bonusPoints || calculation.data.statusCredits)
    })
  }, [pointsCalculations])

  return <Modal
    title={title}
    subtitle="Below is a breakdown of points and status credits earned."
  >
    <Group direction="vertical" gap={16}>
      <Table>
        <TableHeader>
          <TableHeaderRow>
            <TableHeaderCell>Package</TableHeaderCell>
            <TableHeaderCell minWidth={80}>Points</TableHeaderCell>
            {bonusPointsCampaign && <TableHeaderCell minWidth={92}><Colour value="highlight-secondary">Bonus Points</Colour></TableHeaderCell>}
            <TableHeaderCell>Status Credits</TableHeaderCell>
          </TableHeaderRow>
        </TableHeader>
        <TableBody>
          {pointsCalculationsToDisplay.map((calculation, index) => {
            const Icon = getLoyaltyProductTypeIcon(calculation?.data?.request.loyaltyProductType)

            return <TableBodyRow key={index}>
              <TableBodyCell>
                <BodyTextBlock variant="small" startIcon={<Icon />}>
                  {getItemTitle(calculation?.data?.request.loyaltyProductType)}
                </BodyTextBlock>
              </TableBodyCell>
              <TableBodyCell>
                {!!calculation?.data?.basePoints && <FormatLoyaltyPoints abbreviated points={calculation.data.basePoints} />}
                {!calculation?.data?.basePoints && <>--</>}
              </TableBodyCell>
              {bonusPointsCampaign && <TableBodyCell>
                {!!calculation?.data?.bonusPoints && <Colour value="highlight-secondary"><FormatLoyaltyPoints abbreviated points={calculation.data.bonusPoints} /></Colour>}
                {!calculation?.data?.bonusPoints && <>--</>}
              </TableBodyCell>}
              <TableBodyCell>
                {!!calculation?.data?.statusCredits && <FormatDecimal value={calculation.data.statusCredits} />}
                {!calculation?.data?.statusCredits && <>--</>}
              </TableBodyCell>
            </TableBodyRow>
          })}
        </TableBody>
      </Table>
      {bonusPointsCampaign && <LoyaltyBonusPointsInfoSheet campaignType={bonusPointsCampaign} />}
      <BodyText variant="small" colour="neutral-two">
        Please note that the final points balance will be credited to your account at the end of your trip and may be different from what you see here.
      </BodyText>
      <BodyText variant="small" colour="neutral-two">
        You will not earn points on any portion or whole item that uses points to pay. Points can only be earned on the remaining dollar value. Some payments types are not eligible for earning points such as promotional codes, credits received before {getLoyaltyProgramDisplayName()} launched, credits received due to cancellation of a booking before {getLoyaltyProgramDisplayName()} launched.
      </BodyText>
    </Group>
  </Modal>
}

export default LoyaltyPointsEarnBreakdownModal
